import PsychologyIcon from "@mui/icons-material/Psychology";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axiosInstance from "../../Axios.jsx";
import { TopWidgetsStudent } from "../../components/topWidgetsStudent/TopWidgetsStudent";
import useAuth from "../../contexts/UserContext.tsx";
import {
  addDistrict,
  addSchoolType,
  selectDistrict,
  selectSchoolType,
} from "../../features/student/studentSlice.js";

function CustomToolbar() {
  return (
    <GridToolbarContainer
      sx={{ padding: 2, display: "flex", justifyContent: "space-between" }}
    >
      <GridToolbarQuickFilter />
      <div className="flex">
        <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
      </div>
    </GridToolbarContainer>
  );
}

export const Student = () => {
  const dispatch = useDispatch();
  const school = useSelector(selectSchoolType);
  const dist = useSelector(selectDistrict);

  const navigate = useNavigate();

  const { user, logout } = useAuth();
  const school_types = user.view_access_list.school_types
    .split(",")
    .map((word) => `'${word}'`)
    .join(",");

  const school_type_array = school_types
    .replaceAll("'", "")
    .split(",")
    .filter((value, index, self) => self.indexOf(value) === index)
    .sort()
    .map((school_type) => ({ label: school_type, value: school_type }));

  const district_names = user.view_access_list.district
    .split(",")
    .map((word) => `'${word}'`)
    .join(",");
  const district_names_array = district_names
    .replaceAll("'", "")
    .split(",")
    .filter((value, index, self) => self.indexOf(value) === index) //Removes duplicates
    .sort()
    .map((district_name) => ({ label: district_name, value: district_name }));

  const [studentData, setStudentData] = useState();
  const [district, setDistrict] = useState(district_names_array[0]);
  const [schoolType, setSchoolType] = useState(school_type_array[0]);
  const [selectedSchoolTypes, setSelectedSchoolTypes] = useState(
    school_type_array.map((option) => option.value)
  );
  const [selectedDistrictNames, setSelectedDistrictNames] = useState(
    district_names_array.map((option) => option.value)
  );
  const [pageSize, setPageSize] = useState(25);

  const getData = async (schoolType, district) => {
    if (school_type_array.length === 0) {
      toast.error("No school types available");
      return;
    }
    if (district_names_array.length === 0) {
      toast.error("No districts available");
      return;
    }

    try {
      let schoolTypeString = selectedSchoolTypes
        .map((type) => `'${type}'`)
        .join(",");
      let districtString = selectedDistrictNames
        .map((dist) => `'${dist}'`)
        .join(",");

      const res = await axiosInstance.post("/getStudentSummary", {
        auth_token: user.auth_token,
        school_types: schoolTypeString,
        district: districtString,
      });
      if (res.data.data.message === "Invalid User") {
        toast.error("Invalid User");
        logout();
        return;
      }

      const int_data_rows = res.data.data.school_details.rows.map((row) => {
        return {
          ...row,
          school_id: parseInt(row.school_id),
          app_student_cnt: parseInt(row.app_student_cnt),
          crs_enrollment_cnt: parseInt(row.crs_enrollment_cnt),
          excs_cnt: parseInt(row.excs_cnt),
        };
      });
      const res_data = {
        ...res.data.data.school_details,
        rows: int_data_rows,
      };
      setStudentData(res_data);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSubmitClick = () => {
    dispatch(addSchoolType(schoolType));
    dispatch(addDistrict(district));
    getData(schoolType, district);
  };

  const handleSelectAllChange = () => {
    // If all checkboxes are already selected, unselect them. Otherwise, select all.
    if (selectedSchoolTypes.length === school_type_array.length) {
      setSelectedSchoolTypes([]);
    } else {
      setSelectedSchoolTypes(school_type_array.map((option) => option.value));
    }
  };

  const handleSelectAllChangeDistrict = () => {
    // If all checkboxes are already selected, unselect them. Otherwise, select all.
    if (selectedDistrictNames.length === district_names_array.length) {
      setSelectedDistrictNames([]);
    } else {
      setSelectedDistrictNames(
        district_names_array.map((option) => option.value)
      );
    }
  };

  const columns = [
    {
      field: "school_name",
      headerName: "School Name",
      minWidth: 400,
      flex: 1,
      renderCell: (params) => (
        <Link
          className="text-[#8884d8] hover:text-[#8884d8] hover:underline"
          to={`/school/${params.row.school_id}`}
        >
          {params.row.school_name}
        </Link>
      ),
    },
    {
      field: "school_id",
      headerName: "School ID",
      width: 200,
      flex: 1,
    },
    {
      field: "app_student_cnt",
      headerName: "No. of Students",
      width: 200,
      flex: 1,
    },
    {
      field: "crs_enrollment_cnt",
      headerName: "No. of Courses Enrolled",
      width: 200,
      flex: 1,
    },
    {
      field: "excs_cnt",
      headerName: "# Exercises Attempted",
      width: 200,
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      flex: 1,
      renderCell: (params) => (
        <div className="flex gap-5 opacity-70">
          <span title="Student Progress">
            <TrendingUpIcon
              onClick={() =>
                navigate(
                  `/student/school/studentprogress/${params.row.school_id}`
                )
              }
              className="hover:cursor-pointer"
            />
          </span>
          <span title="Student Exercise Progress">
            <PsychologyIcon
              className="hover:cursor-pointer"
              onClick={() =>
                navigate(
                  `/student/studentexerciseprogress/${params.row.school_id}`
                )
              }
            />
          </span>
          {/* <span title="Progress Grid View">
            <LeaderboardIcon
              className="hover:cursor-pointer"
              onClick={() =>
                navigate(`/student/progressgrid/${params.row.school_id}`)
              }
            />
          </span> */}
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (school !== "" && dist !== "") {
      setSchoolType(school);
      setDistrict(dist);
      getData(school, dist);
    } else {
      getData(schoolType, district);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="student-page-container">
      <div className="option-selector flex gap-4 mb-4 mt-2">
        <FormControl fullWidth>
          <InputLabel id="checkbox-dropdown-label">School Types</InputLabel>
          <Select
            labelId="checkbox-dropdown-label"
            id="checkbox-dropdown"
            multiple
            label="School Types"
            value={selectedSchoolTypes}
            onChange={() => {}}
            renderValue={(selected) => [...selected].join(", ")}
          >
            <MenuItem key="select-all" onClick={handleSelectAllChange}>
              <FormControlLabel
                control={
                  <Checkbox
                    indeterminate={
                      selectedSchoolTypes.length > 0 &&
                      selectedSchoolTypes.length < school_type_array.length
                    }
                    checked={
                      selectedSchoolTypes.length === school_type_array.length
                    }
                  />
                }
                label="Select All"
              />
            </MenuItem>
            {school_type_array.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedSchoolTypes.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedSchoolTypes([
                            ...selectedSchoolTypes,
                            option.value,
                          ]);
                        } else {
                          setSelectedSchoolTypes(
                            selectedSchoolTypes.filter(
                              (type) => type !== option.value
                            )
                          );
                        }
                      }}
                    />
                  }
                  label={option.label}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="checkbox-dropdown-label">District</InputLabel>
          <Select
            labelId="checkbox-dropdown-label"
            id="checkbox-dropdown"
            multiple
            label="Districts"
            value={selectedDistrictNames}
            onChange={() => {}}
            renderValue={(selected) => [...selected].join(", ")}
          >
            <MenuItem key="select-all" onClick={handleSelectAllChangeDistrict}>
              <FormControlLabel
                control={
                  <Checkbox
                    indeterminate={
                      selectedDistrictNames.length > 0 &&
                      selectedDistrictNames.length < district_names_array.length
                    }
                    checked={
                      selectedDistrictNames.length ===
                      district_names_array.length
                    }
                  />
                }
                label="Select All"
              />
            </MenuItem>
            {district_names_array.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedDistrictNames.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDistrictNames([
                            ...selectedDistrictNames,
                            option.value,
                          ]);
                        } else {
                          setSelectedDistrictNames(
                            selectedDistrictNames.filter(
                              (type) => type !== option.value
                            )
                          );
                        }
                      }}
                    />
                  }
                  label={option.label}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <button
          onClick={handleSubmitClick}
          className="bg-blue-500 rounded-md hover:bg-blue-700 px-4 text-white"
        >
          Submit
        </button>
      </div>

      {studentData && <TopWidgetsStudent data={studentData} />}
      {studentData && (
        <DataGrid
          columns={columns}
          rows={studentData.rows}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          initialState={{
            sorting: {
              sortModel: [{ field: "excs_cnt", sort: "desc" }],
            },
          }}
          pagination
          rowHeight={50}
          sx={{ marginTop: "2.5em" }}
          autoHeight={true}
          getRowId={(row) => row.school_id}
          components={{
            Toolbar: CustomToolbar,
          }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
        />
      )}
    </div>
  );
};
